import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus,
  MagnifyingGlass,
  FunnelSimple,
  CircleNotch,
  Warning,
  Package,
  ArrowUp,
  ArrowDown
} from '@phosphor-icons/react';


export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInventory();
    fetchCategories();
  }, [categoryFilter, showLowStock]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/inventory`;
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (showLowStock) params.append('low_stock', 'true');
      if (search) params.append('search', search);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInventory();
  };

  const isLowStock = (item) => item.quantity <= item.min_stock_level;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
          <p className="text-slate-500">Gestiona tus productos y materiales</p>
        </div>
        <Link
          to="/inventory/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          data-testid="new-item-button"
        >
          <Plus size={20} weight="bold" />
          Nuevo Producto
        </Link>
      </div>

      {/* Low Stock Alert */}
      {items.some(isLowStock) && !showLowStock && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
          <Warning size={24} className="text-orange-500" />
          <div className="flex-1">
            <p className="font-medium text-orange-800">
              {items.filter(isLowStock).length} productos con stock bajo
            </p>
          </div>
          <button
            onClick={() => setShowLowStock(true)}
            className="text-orange-600 hover:text-orange-700 font-medium text-sm"
          >
            Ver todos
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelSimple size={20} className="text-slate-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="">Todas las categorías</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={showLowStock}
                onChange={(e) => setShowLowStock(e.target.checked)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Stock bajo</span>
            </label>
            <button
              type="submit"
              className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              Buscar
            </button>
          </div>
        </form>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Producto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    SKU
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Categoría
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-slate-500">
                    Cantidad
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                    Costo
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                    Precio Venta
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/inventory/${item.id}`)}
                    className={`cursor-pointer transition-colors ${
                      isLowStock(item) ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`inventory-row-${item.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isLowStock(item) ? 'bg-orange-100' : 'bg-slate-100'}`}>
                          <Package size={20} className={isLowStock(item) ? 'text-orange-600' : 'text-slate-500'} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{item.name}</p>
                          {item.location && (
                            <p className="text-xs text-slate-500">Ubicación: {item.location}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                      {item.sku}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className={`font-semibold ${isLowStock(item) ? 'text-orange-600' : 'text-slate-900'}`}>
                          {item.quantity}
                        </span>
                        <span className="text-slate-400 text-sm">/ {item.min_stock_level}</span>
                        {isLowStock(item) && (
                          <Warning size={16} className="text-orange-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-slate-600">
                      ${item.cost_price?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-slate-900">
                      ${item.sell_price?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <Package size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No hay productos en el inventario</p>
            <Link
              to="/inventory/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Agregar primer producto
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
